import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MESSAGES from '../../../../common/data/constants/messages';
import { IEmployee } from '../../../../common/data/dtos/employee';
import { ITrainingEmployee } from '../../../../common/data/dtos/training-employee';
import TrainingEmployeeStatusEnum, { getStatusBadge } from '../../../../common/data/enums/enumTrainingEmployeeStatus';
import Badge from '../../../../components/bootstrap/Badge';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardActions, CardBody, CardFooter, CardFooterLeft, CardHeader, CardLabel, CardSubTitle, CardTitle } from '../../../../components/bootstrap/Card';
import { MoreActions } from '../../../../components/extras/MoreActions';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import ThemeContext from '../../../../contexts/themeContext';
import { appMenu } from '../../../../menu';
import employeeService from '../../../../services/employee-service';
import trainingEmployeeService from '../../../../services/training-employee-service';
import LoadingPage from '../../loading/LoadingPage';
import TrainingCompletedModal from './TrainingCompletedModal';
import TrainingEmployeeModal from './TrainingEmployeeModal';

type TTabs = 'Pendentes' | 'Realizados';

interface ITabs {
    [key: string]: TTabs;
}

const CommonTrainingEmployeeList = () => {
    const TABS: ITabs = {
        DONE: 'Realizados',
        PENDING: 'Pendentes',
    };

    const { mobileDesign } = useContext(ThemeContext);
    const { id } = useParams();

    const [activeTab, setActiveTab] = useState<TTabs>(TABS.PENDING);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isModalTrainingOpened, setIsModalTrainingOpened] = useState(false);

    const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(null);
    const [selectedTrainingEmployee, setSelectedTrainingEmployee] = useState<ITrainingEmployee | null>(null);

    const [doneTrainingEmployees, setDoneTrainingEmployees] = useState<ITrainingEmployee[]>([]);
    const [pendingTrainingEmployees, setPendingTrainingEmployees] = useState<ITrainingEmployee[]>([]);
    const [trainingEmployees, setTrainingEmployees] = useState<ITrainingEmployee[]>([]);

    useEffect(() => {
        if (id) {
            fetchData();
            setSelectedTrainingEmployee(null);
        }
    }, []);

    const fetchData = async () => {
        setIsLoading(true);

        try {
            if (!id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            } else {
                const [employeeResponse, trainingEmployeeResponse] = await Promise.all([employeeService.getOneEmployee(id), trainingEmployeeService.getTrainingEmployeeByEmployee(id)]);

                if (employeeResponse.error) {
                    showWarning(employeeResponse.error);
                    return;
                }

                if (trainingEmployeeResponse.error) {
                    showWarning(trainingEmployeeResponse.error);
                    return;
                }

                setSelectedEmployee(employeeResponse.employee || null);

                if (trainingEmployeeResponse.trainingEmployees) {
                    setTrainingEmployees(trainingEmployeeResponse.trainingEmployees);
                } else {
                    setTrainingEmployees([]);
                }
            }
        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTrainingEmployees = async () => {
        setIsLoading(true);

        if (!id) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            setIsLoading(false);
        } else {
            const response = await trainingEmployeeService.getTrainingEmployeeByEmployee(id);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            if (response.trainingEmployees) {
                // const pending = response.trainingEmployees.filter((item) => item.situation === TrainingEmployeeStatusEnum.pending);
                // const done = response.trainingEmployees.filter((item) => item.situation === TrainingEmployeeStatusEnum.done);

                // setPendingTrainingEmployees(pending || []);
                // setDoneTrainingEmployees(done || []);
                setTrainingEmployees(response.trainingEmployees || []);
            } else {
                // setPendingTrainingEmployees([]);
                // setDoneTrainingEmployees([]);
                setTrainingEmployees([]);
            }
        }
    };

    const toggleModal = (setter: React.Dispatch<React.SetStateAction<boolean>>, isVisible: boolean) => {
        setter(isVisible);
    };

    const handleAddClick = () => {
        toggleModal(setIsModalOpened, true);
    };

    const handleDeleteClick = async (trainingEmployee: ITrainingEmployee) => {
        if (!trainingEmployee.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
        } else {
            setIsLoading(true);

            const response = await trainingEmployeeService.deleteTrainingEmployee(trainingEmployee.id);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchTrainingEmployees();
        }
    };

    const onModalClose = () => {
        toggleModal(setIsModalOpened, false);
    };

    const onTrainingModalClose = () => {
        toggleModal(setIsModalTrainingOpened, false);
        setSelectedTrainingEmployee(null);
    };

    const onAfterSubmit = async () => {
        await fetchTrainingEmployees();
        onModalClose();
    };

    const onTrainingAfterSubmit = async () => {
        await fetchTrainingEmployees();
        onTrainingModalClose();
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    const handleDoneClick = (item: ITrainingEmployee) => {
        setSelectedTrainingEmployee(item);
        toggleModal(setIsModalTrainingOpened, true);
    };

    const handleCancelDoneClick = (item: ITrainingEmployee) => {};

    return (
        <>
            <div className="row h-100">
                <div className="col-xxl-3 col-xl-3 col-lg-4 col-md-4 col-sm-12">
                    <Card stretch style={mobileDesign ? { minHeight: 200 } : { minHeight: 400 }}>
                        <CardHeader>
                            <CardLabel icon="Person" iconColor="primary">
                                <CardTitle tag="div" className="h5">
                                    {selectedEmployee?.shortName}
                                </CardTitle>
                                {selectedEmployee?.jobTitle && (
                                    <CardSubTitle tag="div" className="h6">
                                        {selectedEmployee?.jobTitle?.name}
                                    </CardSubTitle>
                                )}
                            </CardLabel>
                        </CardHeader>
                        <CardBody isScrollable>
                            <div className="row g-3">
                                <div className="col-12">
                                    <Button icon="School" color="primary" className="w-100 p-3" isLight={TABS.PENDING !== activeTab} onClick={() => setActiveTab(TABS.PENDING)}>
                                        Treinamentos
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                        <CardFooter>
                            <CardFooterLeft className="w-100">
                                <Button icon="ArrowBack" color="danger" isLight className="w-100 p-3" tag="a" to={`../${appMenu.registers.subMenu.employees.path}`}>
                                    Voltar
                                </Button>
                            </CardFooterLeft>
                        </CardFooter>
                    </Card>
                </div>

                <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-8 col-sm-12">
                    <Card stretch style={{ minHeight: 300 }}>
                        <CardHeader borderSize={1}>
                            <CardLabel icon="School" iconColor="primary">
                                <CardTitle tag="div" className="h5">
                                    Treinamentos realizados
                                </CardTitle>
                            </CardLabel>
                            <CardActions>
                                <Button color="light" icon="AddCircle" onClick={handleAddClick}>
                                    Adicionar
                                </Button>
                            </CardActions>
                        </CardHeader>
                        <CardBody isScrollable className="table-responsive">
                            <table className="table table-modern table-hover">
                                <thead>
                                    <tr>
                                        <th aria-label="Índice" />
                                        <th>Treinamento</th>
                                        <th>Data da Conclusão</th>
                                        <th>Válido até</th>
                                        <th>Situação</th>
                                        <td aria-labelledby="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {trainingEmployees?.map((item: ITrainingEmployee, index) => (
                                        <tr key={item.id}>
                                            <td style={{ width: 40 }}>{index + 1}</td>
                                            <td>{item.training?.name}</td>
                                            <td>{item.doneDate ? dayjs(item.doneDate).format('DD/MM/YYYY') : ''}</td>
                                            <td>
                                                {item.doneDate
                                                    ? dayjs(item.doneDate)
                                                        .add(item.training?.validity ?? 0, 'day')
                                                        .format('DD/MM/YYYY')
                                                    : ''}
                                            </td>
                                            <td>
                                                <h5>
                                                    {(() => {
                                                        const { label, color } = getStatusBadge(item.situation);
                                                        return (
                                                            <Badge color={color} className="align-items-center justify-content-center">
                                                                {label}
                                                            </Badge>
                                                        );
                                                    })()}
                                                </h5>
                                            </td>
                                            {item.situation === TrainingEmployeeStatusEnum.pending && (
                                                <MoreActions
                                                    id={item.id}
                                                    showDelete
                                                    onDeleteClick={() => handleDeleteClick(item)}
                                                    customActions={[
                                                        {
                                                            label: 'Concluir',
                                                            icon: 'Done',
                                                            onClickAction: () => handleDoneClick(item),
                                                        },
                                                    ]}
                                                />
                                            )}
                                            {item.situation === TrainingEmployeeStatusEnum.done && (
                                                <MoreActions
                                                    id={item.id}
                                                    showDelete
                                                    onDeleteClick={() => handleDeleteClick(item)}
                                                />
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardBody>
                    </Card>
                </div>
            </div>

            <TrainingEmployeeModal isModalOpened={isModalOpened} onModalClose={onModalClose} onAfterSubmit={onAfterSubmit} selectedEmployee={selectedEmployee} />
            <TrainingCompletedModal isModalOpened={isModalTrainingOpened} selectedTrainingEmployee={selectedTrainingEmployee} onModalClose={onTrainingModalClose} onAfterSubmit={onTrainingAfterSubmit} />
        </>
    );
};

export default CommonTrainingEmployeeList;
